"use client";

import { getLink } from "@utils/linkUtils";
import classNames from "classnames";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { FC, useContext, useEffect, useMemo, useState } from "react";

import "@/components/ContactChannels/ContactChannels.css";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import { ModalsContext } from "@/context/ModalsContext";
import { UTMParametersContext, getFallbackCampaignParameters } from "@/context/UTMParametersContext";
import { contactChannelTracking, ctaClickTracking } from "@/dataLayer/dataLayer";
import { DL_KEYS } from "@/dataLayer/keys";
import { CustomLink } from "@/types";
import cn from "@/utils/cn";

type ContactChannelsProps = {
  customClass?: string;
  variation?: string;
  contactChannels: CustomLink[];
  desktop?: boolean;
  isCorporate?: boolean;
};

export const handleContactChannelTracking = (icon: string, projectSlug: string) => {
  const iconName = icon?.split(".")[0];
  let channel = "";
  switch (iconName) {
    case "phone":
      channel = "Phone";
      break;
    case "calendar":
      channel = "Get in Touch";
      break;
    case "social-whatsapp":
      channel = "WhatsApp";
      break;

    default:
      break;
  }
  contactChannelTracking(channel, projectSlug || "");
};

const ContactChannels: FC<ContactChannelsProps> = ({ customClass, contactChannels, variation, desktop, isCorporate }) => {
  const locale = useLocale();
  const pathname = usePathname();
  const t = useTranslations();
  const { setGetInTouchModalOpen, setGetFormPageLocation, setScheduleCallModalOpen } = useContext(ModalsContext);
  const [pageTitle, setPageTitle] = useState("");
  const { utmParameters } = useContext(UTMParametersContext);
  const projectSlug = pathname?.split("/")?.find((segment, idx, arr) => arr[idx - 1] === "projects" || arr[idx - 1] === "communities");

  // Takes the name of the property from the metadata and sets the label that is sent with the WhatsApp message
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window?.location?.pathname?.includes("projects")) {
        const title = document.title;
        setPageTitle(title);
      }
    }
  }, []);

  // Retrieves parameters from the localStorage and converts them into query strings
  const getLocalStorageParams = () => {
    if (typeof window !== "undefined") {
      const params = localStorage.getItem("utmParameters");
      if (params) {
        const parsedParams = JSON.parse(params);
        const searchParams = new URLSearchParams(parsedParams);
        return searchParams.toString();
      }
    }
  };

  // Gets and set UTM Parameters
  const parameters: { [key: string]: string } = {
    ...getFallbackCampaignParameters(utmParameters?.utm_medium),
    ...utmParameters,
  };

  const whatsappURL = useMemo(() => {
    if (typeof window !== "undefined") {
      // Get base URL without query parameters
      const currentUrl = new URL(window.location.href);
      const baseUrl = `${currentUrl.origin}${currentUrl.pathname}`;

      // Parse existing URL parameters
      const currentParams = new URLSearchParams(currentUrl.search);

      // Get localStorage parameters
      const localStorageParams = getLocalStorageParams();

      // Properly type the object with index signature
      const localStorageParamsObj: Record<string, string> = {};

      if (localStorageParams) {
        // Convert localStorageParams string to object
        const searchParams = new URLSearchParams(localStorageParams);
        searchParams.forEach((value, key) => {
          localStorageParamsObj[key] = value;
        });
      }

      // Create a new URLSearchParams instance for the final parameters
      const finalParams = new URLSearchParams();

      // Priority: localStorage parameters over URL parameters
      // Add current URL parameters first (lower priority)
      currentParams.forEach((value, key) => {
        finalParams.append(key, value);
      });

      // Then override with localStorage parameters (higher priority)
      Object.entries(localStorageParamsObj).forEach(([key, value]) => {
        finalParams.set(key, String(value)); // Explicitly convert to string
      });

      // Construct the final landing page URL - CHECK IF PARAMS EXIST TO AVOID ?&
      const paramsString = finalParams.toString();
      const finalLandingPageURL = paramsString ? `${baseUrl}?${paramsString}` : baseUrl;

      // Create search params from campaign parameters
      const searchParams = new URLSearchParams(
        parameters["campaign_id"] === "a120Y000000uLMj" ? { campaign_id: parameters["campaign_id"] } : parameters,
      );
      searchParams.delete("fullLandingPageUrl");
      searchParams.delete("landingPageURL");

      // Check if the URL already contains utm_medium or campaign_id
      // Parse the finalLandingPageURL to check for duplicate parameters
      const finalUrlObj = new URL(finalLandingPageURL);
      const finalUrlParams = finalUrlObj.searchParams;

      // When constructing the final WhatsApp URL text, check if parameters already exist
      const textUrlParams = new URLSearchParams(searchParams.toString());

      // Specifically check and remove duplicates for utm_medium and campaign_id
      if (finalUrlParams.has("utm_medium") && textUrlParams.has("utm_medium")) {
        // Keep the one from finalUrlParams (from localStorage/URL) and remove from textUrlParams
        textUrlParams.delete("utm_medium");
      }

      if (finalUrlParams.has("campaign_id") && textUrlParams.has("campaign_id")) {
        // Keep the one from finalUrlParams (from localStorage/URL) and remove from textUrlParams
        textUrlParams.delete("campaign_id");
      }

      return {
        finalLandingPageURL,
        searchParams: textUrlParams,
      };
    }
    return undefined;
  }, [parameters]);
  const whatsappNumber = t("label.whatsapp.number") || "97144016335";

  // console.log("whatsappURL", whatsappURL);
  return (
    <nav className={cn("flex flex-row md:inline-flex md:flex-row md:gap-l stroke-white", customClass)}>
      <Button
        className="p-s md:p-0 no-underline flex items-center md:inline-block whatsapp-button"
        size={ButtonSize.MEDIUM}
        theme={ButtonTheme.WHITE}
        type="button"
        variant={ButtonVariant.TERTIARY}
        href={
          !!whatsappURL
            ? `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${
                pathname?.includes("projects")
                  ? encodeURIComponent(
                      t("text.project.pages", {
                        projectName: pageTitle,
                        url: `${whatsappURL?.finalLandingPageURL}${whatsappURL?.finalLandingPageURL?.includes("?") ? "&" : "?"}${whatsappURL?.searchParams}`,
                      }),
                    )
                  : encodeURIComponent(
                      t("text.generic.pages", {
                        url: `${whatsappURL?.finalLandingPageURL}${whatsappURL?.finalLandingPageURL?.includes("?") ? "&" : "?"}${whatsappURL?.searchParams}`,
                      }),
                    )
              }`
            : t("text.generic.pages", {
                url: `https://api.whatsapp.com/send?phone=${whatsappNumber}&campaign_id=${parameters["campaign_id"]}`,
              })
        }
        onClick={() => {
          handleContactChannelTracking("social-whatsapp", projectSlug || "");
          ctaClickTracking("WhatsApp", "ContactChannels", "ContactChannels");
        }}
      >
        <span className="flex items-center justify-between gap-xs w-full">
          <span className={`${customClass ? customClass : ""}`}>{t("label.common.whatsapp")}</span>
          <Icon
            className={`stroke-black ${variation ? variation : ""}`}
            name="social-whatsapp"
            size={`${desktop ? "20" : "24"}`}
            title={t("label.common.whatsapp")}
          />
        </span>
      </Button>

      <Button
        className="p-s md:p-0 no-underline flex items-center md:inline-block whatsapp-button relative"
        size={ButtonSize.MEDIUM}
        theme={ButtonTheme.WHITE}
        type="button"
        variant={ButtonVariant.TERTIARY}
        // onKeyDown={(event) => {
        //   event.preventDefault();
        //   ctaClickTracking(t("label.common.callus"), "ContactChannels", "ContactChannels");
        // }}
        // BUG-139372 - PRODUCTION - Change Request - Remove "Call Us" from Tooltip.
        href={`tel:${getFallbackCampaignParameters(utmParameters?.utm_medium)?.virtual_number || t("label.common.callcenter") || "+97145205400"}`}
        poneNumberCta={() => {
          contactChannelTracking(DL_KEYS?.callUs, projectSlug || "");
          ctaClickTracking(DL_KEYS?.callUs, "ContactChannels", "ContactChannels");
        }}
      >
        <span className="flex items-center justify-between gap-xs w-full">
          <span className={`${customClass ? customClass : ""}`}>{t("label.common.callus")}</span>
          <Icon
            className={`stroke-black ${variation ? variation : ""}`}
            name="phone"
            size={`${desktop ? "20" : "24"}`}
            title={t("label.common.phone")}
          />
        </span>
      </Button>

      {contactChannels?.map((contactChannel) => (
        <Button
          key={contactChannel.id}
          href={
            contactChannel?.link?.linkType === "external" && contactChannel?.link?.url === "get-in-touch"
              ? undefined
              : getLink(contactChannel, locale)
          }
          className={classNames(
            "p-s md:p-0 no-underline flex items-center md:inline-block contact-channel-button relative",
            isCorporate && "pl-s py-xxs pr-xxs",
            contactChannel?.label?.toLowerCase()?.replace(/\s+/g, "-"), // To add unique class each button to set right order
          )}
          size={ButtonSize.MEDIUM}
          theme={ButtonTheme.WHITE}
          type="button"
          variant={ButtonVariant.TERTIARY}
          onClick={() => {
            handleContactChannelTracking(contactChannel?.link?.icon ?? "", projectSlug || "");
            if ((contactChannel?.link?.icon ?? "").indexOf("calendar") >= 0) {
              setGetInTouchModalOpen(true);
              setGetFormPageLocation("Header Lead Form");
            }
            if ((contactChannel?.link?.icon ?? "").indexOf("Support_services") >= 0) {
              setScheduleCallModalOpen(true);
              setGetFormPageLocation("Header Callus Form");
            }
            ctaClickTracking(contactChannel?.label, "ContactChannels", "ContactChannels");
          }}
        >
          <span className={classNames("flex items-center justify-between gap-xs w-full", { "font-medium": isCorporate })}>
            <span className={`${customClass ? customClass : ""}`}>{contactChannel?.label}</span>
            <Icon
              className={`stroke-black ${variation ? variation : ""}`}
              name={`${contactChannel.link.icon}`}
              size={`${desktop ? "20" : "24"}`}
              title={`${contactChannel.link.icon}`}
            />
          </span>
        </Button>
      ))}
    </nav>
  );
};
export default ContactChannels;
